import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { z } from 'zod';
import {
  AdminOverviewResponseSchema,
  AdminReportsResponseSchema,
  AdminUsersResponseSchema,
  AdminUserDetailResponseSchema,
  AdminRoomsResponseSchema,
  AdminProResponseSchema,
  AdminMediaResponseSchema,
  AdminMediaBulkDeleteResponseSchema,
  AdminSpotsResponseSchema,
  AuditResponseSchema,
  AdminConfigResponseSchema,
  BlocklistResponseSchema,
  ModeratorsResponseSchema,
  MetricsResponseSchema,
  BroadcastResultSchema,
  BroadcastHistoryResponseSchema,
  AdminAppealsResponseSchema,
  type TakeAction,
  type BulkReportAction,
  type AdminUserAction,
  type AdminUserFilter,
  type AddModNote,
  type AdminRoomAction,
  type AdminProAction,
  type AdminMediaDelete,
  type AdminMediaBulkDelete,
  type AdminSpotAction,
  type AdminConfig,
  type UpsertBlocklistZone,
  type SetModerator,
  type Broadcast,
  type AppealDecision,
} from '@web/shared';
import { apiRequest, fetchAuthedBlob } from '@/lib/api';

const ok = z.object({ ok: z.boolean() });

/** Common paging params every admin list query carries. */
export interface PageArgs {
  cursor?: string;
  limit?: number;
}

const qs = (params: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  return p.toString();
};

export const useAdminOverview = () =>
  useQuery({
    queryKey: ['admin', 'overview'],
    refetchInterval: 30_000,
    queryFn: () => apiRequest('/api/admin/overview', { schema: AdminOverviewResponseSchema }),
  });

/* ---------------- reports ---------------- */

export interface ReportQuery extends PageArgs {
  status: 'open' | 'actioned' | 'dismissed';
  reason?: string;
  severity?: string;
  target?: string;
  groupBy?: 'user';
}

export const useAdminReports = (q: ReportQuery) =>
  useQuery({
    queryKey: ['admin', 'reports', q],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiRequest(`/api/admin/reports?${qs({ ...q })}`, { schema: AdminReportsResponseSchema }),
  });

const invalidateReports = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({ queryKey: ['admin', 'reports'] });
  void qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
};

/** Lazily resolves the one image a report can carry — only reachable via
 * `/api/admin/reports/:id/image`, gated on that exact report having an
 * attached image. No bucket-listing endpoint exists. */
export function useReportImage(reportId: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!reportId) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchAuthedBlob(`/api/admin/reports/${reportId}/image`).then(
      (blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reportId]);
  return url;
}

export function useReportAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TakeAction }) =>
      apiRequest(`/api/admin/reports/${id}/action`, { method: 'POST', body, schema: ok }),
    onSuccess: () => invalidateReports(qc),
  });
}

export function useBulkReportAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkReportAction) =>
      apiRequest('/api/admin/reports/bulk-action', { method: 'POST', body, schema: ok }),
    onSuccess: () => invalidateReports(qc),
  });
}

export function useReportClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, claim }: { id: string; claim: boolean }) =>
      apiRequest(`/api/admin/reports/${id}/${claim ? 'claim' : 'release'}`, {
        method: 'POST',
        schema: ok,
      }),
    onSuccess: () => invalidateReports(qc),
  });
}

export function useReportEscalate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/api/admin/reports/${id}/escalate`, { method: 'POST', schema: ok }),
    onSuccess: () => invalidateReports(qc),
  });
}

/* ---------------- users ---------------- */

export interface UserQuery extends PageArgs {
  q?: string;
  filter: AdminUserFilter;
}

export const useAdminUsers = (q: UserQuery) =>
  useQuery({
    queryKey: ['admin', 'users', q],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiRequest(`/api/admin/users?${qs({ ...q })}`, { schema: AdminUsersResponseSchema }),
  });

export const useAdminUser = (id: string | null) =>
  useQuery({
    queryKey: ['admin', 'user', id],
    enabled: !!id,
    queryFn: () => apiRequest(`/api/admin/users/${id}`, { schema: AdminUserDetailResponseSchema }),
  });

const invalidateUser = (qc: ReturnType<typeof useQueryClient>, id?: string) => {
  qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  if (id) qc.invalidateQueries({ queryKey: ['admin', 'user', id] });
};

export function useUserAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdminUserAction }) =>
      apiRequest(`/api/admin/users/${id}/action`, { method: 'POST', body, schema: ok }),
    onSuccess: (_d, { id }) => invalidateUser(qc, id),
  });
}

export function useAddModNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AddModNote }) =>
      apiRequest(`/api/admin/users/${id}/note`, { method: 'POST', body, schema: ok }),
    onSuccess: (_d, { id }) => invalidateUser(qc, id),
  });
}

export function useDeleteModNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, noteId }: { id: string; noteId: string }) =>
      apiRequest(`/api/admin/users/${id}/note/${noteId}`, { method: 'DELETE', schema: ok }),
    onSuccess: (_d, { id }) => invalidateUser(qc, id),
  });
}

/* ---------------- rooms ---------------- */

export interface RoomQuery extends PageArgs {
  filter: 'all' | 'pending' | 'active' | 'reported' | 'frozen' | 'archived';
  q?: string;
}

export const useAdminRooms = (q: RoomQuery) =>
  useQuery({
    queryKey: ['admin', 'rooms', q],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiRequest(`/api/admin/rooms?${qs({ ...q })}`, { schema: AdminRoomsResponseSchema }),
  });

export function useRoomAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdminRoomAction }) =>
      apiRequest(`/api/admin/rooms/${id}/action`, { method: 'POST', body, schema: ok }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'rooms'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}

/* ---------------- Pro (access queue) ---------------- */

export interface ProQuery extends PageArgs {
  filter: 'all' | 'requested' | 'active' | 'denied' | 'revoked';
  q?: string;
}

export const useAdminProRequests = (q: ProQuery) =>
  useQuery({
    queryKey: ['admin', 'pro', q],
    placeholderData: keepPreviousData,
    queryFn: () => apiRequest(`/api/admin/pro?${qs({ ...q })}`, { schema: AdminProResponseSchema }),
  });

export function useProAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: AdminProAction }) =>
      apiRequest(`/api/admin/pro/${userId}/action`, { method: 'POST', body, schema: ok }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'pro'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}

/* ---------------- media (R2 bucket manager) ---------------- */

export interface MediaQuery extends PageArgs {
  filter: 'all' | 'whisper' | 'pro' | 'deleted';
}

export const useAdminMedia = (q: MediaQuery) =>
  useQuery({
    queryKey: ['admin', 'media', q],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiRequest(`/api/admin/media?${qs({ ...q })}`, { schema: AdminMediaResponseSchema }),
  });

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }: { key: string; body: AdminMediaDelete }) =>
      apiRequest(`/api/admin/media/${encodeURIComponent(key)}/delete`, {
        method: 'POST',
        body,
        schema: ok,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'media'] }),
  });
}

export function useBulkDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminMediaBulkDelete) =>
      apiRequest('/api/admin/media/bulk-delete', {
        method: 'POST',
        body,
        schema: AdminMediaBulkDeleteResponseSchema,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'media'] }),
  });
}

/* ---------------- spots ---------------- */

export interface SpotQuery extends PageArgs {
  filter: 'all' | 'pending' | 'disabled';
  q?: string;
}

export const useAdminSpots = (q: SpotQuery) =>
  useQuery({
    queryKey: ['admin', 'spots', q],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiRequest(`/api/admin/spots?${qs({ ...q })}`, { schema: AdminSpotsResponseSchema }),
  });

export function useSpotAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdminSpotAction }) =>
      apiRequest(`/api/admin/spots/${id}/action`, { method: 'POST', body, schema: ok }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'spots'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}

export const useBlocklist = () =>
  useQuery({
    queryKey: ['admin', 'blocklist'],
    queryFn: () => apiRequest('/api/admin/blocklist', { schema: BlocklistResponseSchema }),
  });

export function useUpsertBlocklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertBlocklistZone) =>
      apiRequest('/api/admin/blocklist', {
        method: 'POST',
        body,
        schema: z.object({ ok: z.boolean(), id: z.string() }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blocklist'] }),
  });
}

export function useDeleteBlocklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/api/admin/blocklist/${id}`, { method: 'DELETE', schema: ok }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blocklist'] }),
  });
}

/* ---------------- audit + config ---------------- */

export interface AuditQuery extends PageArgs {
  actor?: string;
  action?: string;
  targetType?: string;
  since?: string;
}

export const useAudit = (q: AuditQuery) =>
  useQuery({
    queryKey: ['admin', 'audit', q],
    placeholderData: keepPreviousData,
    queryFn: () => apiRequest(`/api/admin/audit?${qs({ ...q })}`, { schema: AuditResponseSchema }),
  });

export const useAdminConfig = () =>
  useQuery({
    queryKey: ['admin', 'config'],
    queryFn: () => apiRequest('/api/admin/config', { schema: AdminConfigResponseSchema }),
  });

export function useUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AdminConfig>) =>
      apiRequest('/api/admin/config', { method: 'PATCH', body, schema: AdminConfigResponseSchema }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'config'] }),
  });
}

/* ---------------- moderators ---------------- */

export const useModerators = () =>
  useQuery({
    queryKey: ['admin', 'moderators'],
    queryFn: () => apiRequest('/api/admin/moderators', { schema: ModeratorsResponseSchema }),
  });

export function useSetModerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetModerator) =>
      apiRequest('/api/admin/moderators', { method: 'POST', body, schema: ok }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'moderators'] }),
  });
}

/* ---------------- metrics ---------------- */

export const useMetrics = (range: '7d' | '30d') =>
  useQuery({
    queryKey: ['admin', 'metrics', range],
    queryFn: () =>
      apiRequest(`/api/admin/metrics?range=${range}`, { schema: MetricsResponseSchema }),
  });

/* ---------------- broadcast ---------------- */

export const useBroadcasts = (q: PageArgs) =>
  useQuery({
    queryKey: ['admin', 'broadcasts', q],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiRequest(`/api/admin/broadcast?${qs({ ...q })}`, {
        schema: BroadcastHistoryResponseSchema,
      }),
  });

export function useSendBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Broadcast) =>
      apiRequest('/api/admin/broadcast', { method: 'POST', body, schema: BroadcastResultSchema }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'broadcasts'] }),
  });
}

/* ---------------- appeals (review) ---------------- */

export const useAdminAppeals = (q: PageArgs & { status: string }) =>
  useQuery({
    queryKey: ['admin', 'appeals', q],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiRequest(`/api/admin/appeals?${qs({ ...q })}`, { schema: AdminAppealsResponseSchema }),
  });

export function useAppealDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AppealDecision }) =>
      apiRequest(`/api/admin/appeals/${id}/decision`, { method: 'POST', body, schema: ok }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'appeals'] });
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}
